/**
 * Geometry of the power menu (src/components/ui/EspPowerButton.vue): places the entries on
 * half-circle rows above the button so that no two entries overlap. Pure module, positions in
 * px relative to the arc center, x to the right and y upwards.
 */

const RADIUS_STEP = 5;
const SHIFT_STEP = 2;
const TOLERANCE = 0.5;

/**
 * Positions for `count` boxes of itemWidth × itemHeight, each centered on its position, such
 * that no two boxes overlap, none overlaps `keepOut` ({ x, y, width, height }, centered) and
 * none reaches below `minBottom`. The first row is the arc with the smallest radius between
 * `minRadius` and `maxRadius` that holds the most items (up to `maxPerRow`): 120° wide for up
 * to four items when that fits, 170° otherwise. Items beyond that go on rows of the same arc
 * shifted upwards, each at least `rowGap` above the boxes below and as full as the row below
 * allows. Order: first row left to right, then the rows above. With `maxRadius` below
 * `minRadius` the arc keeps `minRadius` and may exceed the available space.
 */
export function arcLayout(options) {
    const { count, itemWidth, itemHeight, minRadius, keepOut = null, maxPerRow = 7, rowGap = 8 } = options;
    if (!(count > 0)) {
        return [];
    }
    const maxRadius = Math.max(options.maxRadius, minRadius);
    const box = {
        width: itemWidth,
        height: itemHeight,
        minBottom: Number.isFinite(options.minBottom) ? options.minBottom : -Infinity,
        keepOut,
    };

    const first = firstRow(count, maxPerRow, minRadius, maxRadius, box);
    const placed = [...first.row];
    let below = first.row.length;
    let shift = 0;
    while (placed.length < count) {
        const base = shift + itemHeight + rowGap;
        let row = null;
        for (let size = Math.min(count - placed.length, below); size >= 1 && !row; size--) {
            for (let candidate = base; candidate <= base + itemHeight / 2 && !row; candidate += SHIFT_STEP) {
                const positions = steppedRow(size, first.radius, first.step, candidate);
                if (fits(positions, placed, box)) {
                    row = positions;
                    shift = candidate;
                }
            }
        }
        if (!row) {
            // a single item on top of the row below always fits; keeps the loop finite
            row = steppedRow(1, first.radius, 0, base);
            shift = base;
        }
        placed.push(...row);
        below = row.length;
    }
    return placed.map((position) => ({ x: Math.round(position.x), y: Math.round(position.y) }));
}

/** Row with the most items that fits at any radius, at the smallest such radius. */
function firstRow(count, maxPerRow, minRadius, maxRadius, box) {
    for (let size = Math.min(count, maxPerRow); size > 1; size--) {
        const spans = size <= 4 ? [120, 170] : [170];
        for (const span of spans) {
            const step = span / (size - 1);
            for (let radius = minRadius; radius <= maxRadius; radius += RADIUS_STEP) {
                const row = steppedRow(size, radius, step, 0);
                if (fits(row, [], box)) {
                    return { row, radius, step };
                }
            }
        }
    }
    return { row: steppedRow(1, minRadius, 0, 0), radius: minRadius, step: 0 };
}

/** `size` positions centered on the top of the arc, `step` degrees apart, left to right, lifted by `shift`. */
function steppedRow(size, radius, step, shift) {
    const positions = [];
    for (let index = 0; index < size; index++) {
        const angle = ((90 + ((size - 1) / 2 - index) * step) * Math.PI) / 180;
        positions.push({ x: Math.cos(angle) * radius, y: Math.sin(angle) * radius + shift });
    }
    return positions;
}

function fits(row, placed, box) {
    return row.every(
        (item, index) =>
            item.y - box.height / 2 >= box.minBottom - TOLERANCE &&
            !(box.keepOut && overlaps(item, box, box.keepOut, box.keepOut)) &&
            row.slice(index + 1).every((other) => !overlaps(item, box, other, box)) &&
            placed.every((other) => !overlaps(item, box, other, box)),
    );
}

function overlaps(a, sizeA, b, sizeB) {
    return (
        Math.abs(a.x - b.x) < (sizeA.width + sizeB.width) / 2 - TOLERANCE &&
        Math.abs(a.y - b.y) < (sizeA.height + sizeB.height) / 2 - TOLERANCE
    );
}
